import { createAsyncThunk } from '@reduxjs/toolkit'
import { goalsAPI } from '../../utils/supabase'

// Available Years
export const fetchAvailableYears = createAsyncThunk(
  'goals/fetchAvailableYears',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.getAvailableYears(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Goal scheduling operations
export const promoteDraftGoal = createAsyncThunk(
  'goals/promoteDraftGoal',
  async ({ goalId, goalType, schedulingData }, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.promoteGoal(goalId, goalType, schedulingData)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const unscheduleGoal = createAsyncThunk(
  'goals/unscheduleGoal',
  async ({ goalId, goalType }, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.cascadeUnscheduleGoal(goalId, goalType)
      if (error) throw error
      return { ...data, goalType }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const cascadeCompleteGoal = createAsyncThunk(
  'goals/cascadeCompleteGoal',
  async ({ goalId, goalType, cascade = true }, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.cascadeCompleteGoal(goalId, goalType, cascade)
      if (error) throw error
      return { ...data, goalType }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateDraftGoalOrder = createAsyncThunk(
  'goals/updateDraftGoalOrder',
  async ({ goalType, goals }, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.updateGoalOrder(goalType, goals)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Goals Utility Reducers
export const goalsUtilityReducers = {
  // Available Years
  [fetchAvailableYears.pending]: (state) => {
    state.loading.years = true
    state.error.years = null
  },
  [fetchAvailableYears.fulfilled]: (state, action) => {
    state.loading.years = false
    state.availableYears = action.payload
    state.initialized.years = true
  },
  [fetchAvailableYears.rejected]: (state, action) => {
    state.loading.years = false
    state.error.years = action.payload
  },

  // Draft Goal Promotion
  [promoteDraftGoal.fulfilled]: (state, action) => {
    const { goalType, data } = action.payload
    let goalArray = ''
    
    switch (goalType) {
      case 'annual':
        goalArray = 'annualGoals'
        break
      case 'quarterly':
        goalArray = 'quarterlyGoals'
        break
      case 'weekly':
        goalArray = 'weeklyGoals'
        break
    }
    
    if (goalArray) {
      const index = state[goalArray].findIndex(goal => goal.id === data.id)
      if (index !== -1) {
        state[goalArray][index] = data
      }
    }
  },
  [promoteDraftGoal.rejected]: (state, action) => {
    console.error('Failed to promote draft goal:', action.payload)
  },

  // Draft Goal Reordering
  [updateDraftGoalOrder.fulfilled]: (state, action) => {
    const { goalType, data } = action.payload
    let goalArray = ''
    
    switch (goalType) {
      case 'annual':
        goalArray = 'annualGoals'
        break
      case 'quarterly':
        goalArray = 'quarterlyGoals'
        break
      case 'weekly':
        goalArray = 'weeklyGoals'
        break
    }
    
    if (goalArray && data) {
      data.forEach(updatedGoal => {
        const index = state[goalArray].findIndex(goal => goal.id === updatedGoal.id)
        if (index !== -1) {
          state[goalArray][index] = { ...state[goalArray][index], ...updatedGoal }
        }
      })
    }
  },
  [updateDraftGoalOrder.rejected]: (state, action) => {
    console.error('Failed to update draft goal order:', action.payload)
  },

  // Goal Unscheduling (with cascade to children)
  [unscheduleGoal.fulfilled]: (state, action) => {
    const { annualGoal, quarterlyGoals, weeklyGoals } = action.payload

    // Update annual goal if present
    if (annualGoal) {
      const index = state.annualGoals.findIndex(g => g.id === annualGoal.id)
      if (index !== -1) {
        state.annualGoals[index] = annualGoal
      }
    }

    // Update quarterly goals (cascade from annual or direct unschedule)
    if (quarterlyGoals?.length > 0) {
      quarterlyGoals.forEach(updatedGoal => {
        const index = state.quarterlyGoals.findIndex(g => g.id === updatedGoal.id)
        if (index !== -1) {
          state.quarterlyGoals[index] = updatedGoal
        }
      })
    }

    // Update weekly goals (cascade from quarterly or direct unschedule)
    if (weeklyGoals?.length > 0) {
      weeklyGoals.forEach(updatedGoal => {
        const index = state.weeklyGoals.findIndex(g => g.id === updatedGoal.id)
        if (index !== -1) {
          state.weeklyGoals[index] = updatedGoal
        }
      })
    }
  },
  [unscheduleGoal.rejected]: (state, action) => {
    console.error('Failed to unschedule goal:', action.payload)
  },

  // Goal Completion (with cascade to children)
  [cascadeCompleteGoal.fulfilled]: (state, action) => {
    const { annualGoal, quarterlyGoals, weeklyGoals } = action.payload

    // Update annual goal if present
    if (annualGoal) {
      const index = state.annualGoals.findIndex(g => g.id === annualGoal.id)
      if (index !== -1) {
        state.annualGoals[index] = annualGoal
      }
    }

    // Update quarterly goals (cascade from annual or direct complete)
    if (quarterlyGoals?.length > 0) {
      quarterlyGoals.forEach(updatedGoal => {
        const index = state.quarterlyGoals.findIndex(g => g.id === updatedGoal.id)
        if (index !== -1) {
          state.quarterlyGoals[index] = updatedGoal
        }
      })
    }

    // Update weekly goals (cascade from quarterly or annual)
    if (weeklyGoals?.length > 0) {
      weeklyGoals.forEach(updatedGoal => {
        const index = state.weeklyGoals.findIndex(g => g.id === updatedGoal.id)
        if (index !== -1) {
          state.weeklyGoals[index] = updatedGoal
        }
      })
    }
  },
  [cascadeCompleteGoal.rejected]: (state, action) => {
    console.error('Failed to cascade complete goal:', action.payload)
  }
}
